// noinspection JSUnusedGlobalSymbols

import {defineStore} from 'pinia';
import {
  BiologicalSexType,
  CityType,
  CountryType,
  EducationLevelType,
  EthnicityType,
  GenderType,
  IdentificationNumberTypeType, MaritalStatusType,
} from '@common/types';
import {GeoApiService} from '@common/services';

interface IBiologicalSexesByCountry {
  [key: string]: Array<BiologicalSexType>;
}

interface ICitiesByCountry {
  [key: string]: Array<CityType>;
}

interface IEducationLevelsByCountry {
  [key: string]: Array<EducationLevelType>;
}

interface IEthnicitiesByCountry {
  [key: string]: Array<EthnicityType>;
}

interface IGendersByCountry {
  [key: string]: Array<GenderType>;
}

interface IIdentificationNumberTypesByCountry {
  [key: string]: Array<IdentificationNumberTypeType>;
}

interface IMaritalStatusesByCountry {
  [key: string]: Array<MaritalStatusType>;
}

export const useGeoStore = defineStore('geoStore', {
  state: () => {
    return {
      countries: null as Array<CountryType> | null,
      biologicalSexesByCountry: {} as IBiologicalSexesByCountry,
      citiesByCountry: {} as ICitiesByCountry,
      educationLevelsByCountry: {} as IEducationLevelsByCountry,
      ethnicitiesByCountry: {} as IEthnicitiesByCountry,
      gendersByCountry: {} as IGendersByCountry,
      identificationNumberTypes: {} as IIdentificationNumberTypesByCountry,
      maritalStatusesByCountry: {} as IMaritalStatusesByCountry,
    };
  },
  actions: {
    async initializeAsync() {
      if (this.countries !== null) {
        return this.countries as Array<CountryType>;
      }
      this.countries = await GeoApiService.getCountriesAsync();
    },

    async getBiologicalSexesByCountryAsync(alphaCode2: string): Promise<BiologicalSexType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.biologicalSexesByCountry[code] === undefined) {
        this.biologicalSexesByCountry[code] = await GeoApiService.getBiologicalSexesByCountryAsync(code);
      }
      return this.biologicalSexesByCountry[code];
    },

    async getCitiesByCountryAsync(alphaCode2: string): Promise<CityType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.citiesByCountry[code] === undefined) {
        this.citiesByCountry[code] = await GeoApiService.getCitiesByCountryAsync(code);
      }
      return this.citiesByCountry[code];
    },

    getCountries(): CountryType[] {
      return this.countries as Array<CountryType> ?? [];
    },

    getCountryByCode2(alphaCode2: string) {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return undefined;
      }
      return this.getCountries().find(c => c.alphaCode2 === code);
    },

    async getEducationLevelsByCountryAsync(alphaCode2: string): Promise<EducationLevelType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.educationLevelsByCountry[code] === undefined) {
        this.educationLevelsByCountry[code] = await GeoApiService.getEducationLevelsByCountryAsync(code);
      }
      return this.educationLevelsByCountry[code];
    },

    async getEthnicitiesByCountryAsync(alphaCode2: string): Promise<EthnicityType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.ethnicitiesByCountry[code] === undefined) {
        this.ethnicitiesByCountry[code] = await GeoApiService.getEthnicitiesByCountryAsync(code);
      }
      return this.ethnicitiesByCountry[code];
    },

    async getGendersByCountryAsync(alphaCode2: string): Promise<GenderType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.gendersByCountry[code] === undefined) {
        this.gendersByCountry[code] = await GeoApiService.getGendersByCountryAsync(code);
      }
      return this.gendersByCountry[code];
    },
    
    async getIdentificationNumberTypesByCountryAsync(alphaCode2: string): Promise<IdentificationNumberTypeType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.identificationNumberTypes[code] === undefined) {
        this.identificationNumberTypes[code] = await GeoApiService.getIdentificationNumberTypesByCountryAsync(code);
      }
      return this.identificationNumberTypes[code];
    },

    async getMaritalStatusesByCountryAsync(alphaCode2: string): Promise<MaritalStatusType[]> {
      const code = alphaCode2 ?? '';
      if (!code.length) {
        return [];
      }
      if (this.maritalStatusesByCountry[code] === undefined) {
        this.maritalStatusesByCountry[code] = await GeoApiService.getMaritalStatusesByCountryAsync(code);
      }
      return this.maritalStatusesByCountry[code];
    },
  },
});